import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from "@mui/icons-material/Campaign";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RateReviewIcon from "@mui/icons-material/RateReview";
import SendIcon from "@mui/icons-material/Send";
import SmsIcon from "@mui/icons-material/Sms";
import SyncIcon from "@mui/icons-material/Sync";
import VerifiedIcon from "@mui/icons-material/Verified";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  CERT_QUALIFICATIONS,
  CLOTHING_SIZES,
  COMPLIANCE_CHECKED_STATUSES,
  DECLARATIONS,
  DOCTOR_GRADES,
  DRIVING_LICENCE_QUALIFICATION,
  EXPIRY_QUALIFICATIONS,
  HCP_QUALIFICATIONS,
  GMC_QUALIFICATIONS,
  INDEMNITY_PROVIDERS,
  PERSONNEL_QUALIFICATIONS,
  PERSONNEL_ROLES,
  PERSONNEL_STATUSES,
  DEFAULT_PERSONNEL_STATUS,
  QUALIFICATION_PREREQUISITES,
  RETIRED_QUALIFICATIONS,
  SAS_DOCTOR,
  STATUS_COLOR,
  TRAINING_REFERENCE_POSITION,
  VEHICLES,
  isTrainingGrade,
} from "../constants";
import AllocationManager from "./AllocationManager";
import AttendanceDialog from "./AttendanceDialog";
import DocumentUpload from "./DocumentUpload";
import PersonnelDocumentHistory from "./PersonnelDocumentHistory";
import { API_BASE_URL } from "../config";
import {
  certificateExpiryField, certificateField, changeLabel, changeValue, changedFields, fmtDateTime,
  hcpcRegex, regInput, GMC_RE, NMC_RE,
} from "../personnelChanges";
import { notify } from "../notify";
import { useMe } from "../MeContext";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";

const API = `${API_BASE_URL}/personnel`;
const EVENTS_API = `${API_BASE_URL}/events`;
const ATT_API = `${API_BASE_URL}/attendances`;
const QUAL_CERT_API = `${API_BASE_URL}/qualification-certificates`;
const ATT_BLANK = { event_id: "", role: "", status: "" };

const BLANK = {
  forename: "",
  surname: "",
  status: DEFAULT_PERSONNEL_STATUS,
  email: "",
  login_email: "",
  phone: "",
  date_of_birth: "",
  gmc_number: "",
  nmc_pin: "",
  hcpc_number: "",
  gmc_last_checked: "",
  nmc_last_checked: "",
  hcpc_last_checked: "",
  independent_prescriber: false,
  doctor_grade: "",
  doctor_grade_date: "",
  roles: [],
  qualifications: [],
  qualifications_other: "",
  address_line1: "",
  address_line2: "",
  town: "",
  county: "",
  postcode: "",
  added_date: "",
  source: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  reference_name: "",
  reference_position: "",
  reference_email: "",
  clothing_size: "",
  vehicle: "",
  vehicle_registration: "",
  private_notes: "",
  mrt_team: "",
  indemnity_provider: "",
  indemnity_provider_other: "",
  indemnity_expiry: "",
  indemnity_certificate_id: null,
  cv_document_id: null,
  cv_uploaded_date: "",
  cv_not_required: false,
  driving_licence_number: "",
  driving_licence_test_date: "",
  driving_licence_expiry: "",
  driving_licence_front_id: null,
  driving_licence_back_id: null,
};

// The tint on a field the person changed themselves and nobody has reviewed yet.
const changedTint = (theme) => alpha(theme.palette.info.main, 0.12);

// Local YYYY-MM-DD (avoids UTC off-by-one from toISOString).
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Whether a date input's value is a finished date worth saving. A native date
// input reports a complete value the moment its three boxes are filled, so the
// first digit of a year typed as 2026 arrives as year 0002 — and Chrome lets the
// year run to six digits. Anything outside a plausible 4-digit year is somebody
// mid-keystroke, not a date.
const isCompleteDate = (iso) => {
  const year = Number((iso || "").split("-")[0]);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) && year >= 1900 && year <= 2999;
};

// The same ISO date one year earlier (for the "checked within a year" rule).
const oneYearBeforeISO = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return `${y - 1}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

// A person's record, loaded fresh from the server each time it opens so it can be
// raised from anywhere (the roster, Compliance, an event's personnel list) without
// the caller holding the record. The fields save on Save; the Events section
// below them, the qualification certificates and the permission roles each save
// instantly, which is why `onSaved` can fire without the dialog closing.
//
// Props:
//  - personId:  null/undefined (closed) | "new" | a personnel id
//  - onClose:   () => void — after Cancel, Save, Delete or a discarded close
//  - onSaved:   (person) => void — the saved record, for callers holding a list
//  - onDeleted: (id) => void
export default function PersonnelDialog({ personId, onClose, onSaved, onDeleted }) {
  const { can, me } = useMe();
  const fullScreen = useFullScreenDialog();
  const canEdit = can("Personnel.edit");
  const canActions = can("Personnel.actions");
  const canViewPrivate = can("Personnel.viewPrivate");
  const canEditPermissions = can("Personnel.editPermissions");
  const canManageAccess = can("Personnel.manageAppAccess");
  const canReview = can("Personnel.reviewChanges");

  const open = personId !== null && personId !== undefined;
  const isNew = personId === "new";
  // Uploads and the instantly-saved sections need a persisted record.
  const savedId = typeof personId === "number" ? personId : null;

  const [form, setForm] = useState(BLANK);
  const [initialForm, setInitialForm] = useState(BLANK); // form snapshot at open, to detect unsaved changes
  const [loading, setLoading] = useState(false);         // true while the record is being fetched
  const [confirmClose, setConfirmClose] = useState(false); // discard-unsaved-changes prompt
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Save creates the person and Add creates an attendance, so a double-tap on
  // either would make two of it (see useWriteLock).
  const { busy: saving, run: runWrite } = useWriteLock();
  const EMPTY_VERIFY = { loading: false, result: null, error: null };
  const [gmc, setGmc] = useState(EMPTY_VERIFY); // GMC verified in-app; NMC uses a deep-link
  const [hcpc, setHcpc] = useState(EMPTY_VERIFY); // HCPC verified in-app via the register API
  const [mc, setMc] = useState(EMPTY_VERIFY);   // MailChimp lookup/create state
  // Entra tenant access (guest + users group), Super Admin only. `inviteEmail`
  // is whether Microsoft mails them its B2B invitation when we create the guest —
  // off by default, since people are normally told out of band.
  const [access, setAccess] = useState(EMPTY_VERIFY);
  const [inviteEmail, setInviteEmail] = useState(false);
  // "Send message" (SMS / WhatsApp) dialog state.
  const EMPTY_SMS = { open: false, message: "", loading: false, error: null };
  const [sms, setSms] = useState(EMPTY_SMS);
  const [events, setEvents] = useState([]);           // all events (for the attendance dropdown)
  const [attendances, setAttendances] = useState([]); // this person's event attendances
  const [attDraft, setAttDraft] = useState(ATT_BLANK);
  const [attSort, setAttSort] = useState("date_desc"); // date_desc | date_asc
  const [editAtt, setEditAtt] = useState(null); // attendance shown in the edit dialog
  const [cert, setCert] = useState(null);   // uploaded indemnity certificate {id, original_filename}
  const [cvDoc, setCvDoc] = useState(null); // uploaded CV {id, original_filename}
  // Both sides of the driving licence card, held like the CV: the id is on the
  // form (so it saves with the record), the document itself only for the link.
  const [licenceFront, setLicenceFront] = useState(null);
  const [licenceBack, setLicenceBack] = useState(null);
  const [qualCerts, setQualCerts] = useState([]); // per-qualification certs for this person
  // What's typed in a certificate's Expiry box, keyed by qualification. The row
  // above is the saved value, but the box can't render from it: saving reloads
  // the row, and writing a native date input's value from outside discards the
  // digits it is holding — so the year was reset after every second keystroke and
  // 2026 could never be typed. The draft is the only thing `value` reads.
  const [certExpiry, setCertExpiry] = useState({});
  // Bumped when something below has just superseded a document, to reload the
  // history section. The CV and indemnity certificate don't need it: they only
  // move on Save, which closes the dialog.
  const [historyVersion, setHistoryVersion] = useState(0);
  // Whether the "Sign-in address" box is shown. Most people sign in with their
  // contact email, so it stays behind a link unless the record already has one.
  const [loginOpen, setLoginOpen] = useState(false);
  const [permRoles, setPermRoles] = useState([]); // the edited person's global permission roles (Super Admin only)
  const [initialPermRoles, setInitialPermRoles] = useState([]); // snapshot, to detect unsaved changes
  // What the person changed themselves from My details and nobody has reviewed
  // (core/personnel_self_edit.py). Already applied to the record: this is only
  // the notice at the top and the highlighted fields.
  const [pendingChanges, setPendingChanges] = useState([]);
  // When the person made each declaration of suitability. Shown, never edited
  // here: only they may make one (PUT /personnel/self).
  const [declarations, setDeclarations] = useState({});

  const resetVerify = () => {
    setGmc(EMPTY_VERIFY); setHcpc(EMPTY_VERIFY); setMc(EMPTY_VERIFY);
    setAccess(EMPTY_VERIFY); setInviteEmail(true);
  };

  // Blank rather than null in the form, so every field is a controlled string.
  const formFrom = (p) => Object.fromEntries(
    Object.entries(BLANK).map(([k, blank]) => [k, p[k] ?? blank]));

  const refreshAttendances = (id) =>
    axios.get(ATT_API + "/", { params: { personnel_id: id } }).then(res => setAttendances(res.data));

  const refreshQualCerts = (id) =>
    axios.get(QUAL_CERT_API + "/", { params: { personnel_id: id } }).then(res => setQualCerts(res.data));

  // Load on open. The record is fetched rather than passed in, so a caller only
  // needs the id — and what's shown is never a stale copy from a list.
  useEffect(() => {
    if (!open) return;
    resetVerify();
    setSms(EMPTY_SMS);
    setConfirmClose(false);
    setConfirmDelete(false);
    setAttDraft(ATT_BLANK);
    setEditAtt(null);
    setAttendances([]);
    setQualCerts([]);
    setCertExpiry({});
    setLoginOpen(false);
    setPendingChanges([]);
    if (isNew) {
      setForm(BLANK);
      setInitialForm(BLANK);
      setCert(null);
      setCvDoc(null);
      setLicenceFront(null);
      setLicenceBack(null);
      setPermRoles([]);
      setInitialPermRoles([]);
      return;
    }
    setLoading(true);
    axios.get(`${API}/${personId}`).then(res => {
      const p = res.data;
      const loaded = formFrom(p);
      setForm(loaded);
      setInitialForm(loaded);
      setLoginOpen(!!p.login_email);
      setCert(p.indemnity_certificate || null);
      setCvDoc(p.cv_document || null);
      setLicenceFront(p.driving_licence_front || null);
      setLicenceBack(p.driving_licence_back || null);
      setPermRoles(p.permission_roles || []);
      setInitialPermRoles(p.permission_roles || []);
      setDeclarations(Object.fromEntries(DECLARATIONS.map(d => [d.field, p[d.field] || null])));
    }).finally(() => setLoading(false));
    refreshAttendances(personId);
    refreshQualCerts(personId);
    if (canReview) {
      axios.get(`${API}/${personId}/changes`).then(res => setPendingChanges(res.data));
    }
    // The events list is only needed for the "Add event" picker below.
    axios.get(EVENTS_API + "/").then(res => setEvents(res.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId]);

  const close = () => { setConfirmClose(false); onClose(); };

  // Closing (backdrop, Escape or Cancel) warns first if there are unsaved changes.
  // Attendances save instantly, so only the form fields and permission roles matter.
  const isDirty = () =>
    JSON.stringify(form) !== JSON.stringify(initialForm) ||
    JSON.stringify(permRoles) !== JSON.stringify(initialPermRoles);
  const requestClose = () => (isDirty() ? setConfirmClose(true) : close());

  // The person's events by date, newest or oldest first; any undated event goes
  // last either way.
  const eventDateKey = (a) => a.event?.start_date || a.event?.end_date || "";
  const sortedAttendances = [...attendances].sort((a, b) => {
    const ka = eventDateKey(a), kb = eventDateKey(b);
    if (!ka || !kb) return (!ka && !kb) ? 0 : (ka ? -1 : 1);
    return attSort === "date_asc" ? ka.localeCompare(kb) : kb.localeCompare(ka);
  });

  const addAttendance = () => runWrite(() => {
    if (!attDraft.event_id) return null;
    return axios.post(`${ATT_API}/`, {
      personnel_id: savedId,
      event_id: Number(attDraft.event_id),
      role: attDraft.role || null,
      status: attDraft.status || null,
    }).then(res => {
      setAttDraft(ATT_BLANK);
      refreshAttendances(savedId);
      setEditAtt(res.data); // open the dialog so role/status/fee can be set
    });
  });

  const upsertQualCert = (qualification, patch) =>
    axios.post(QUAL_CERT_API + "/", { personnel_id: savedId, qualification, ...patch })
      .then(() => {
        refreshQualCerts(savedId);
        // Replacing a certificate sends the old one to the history below.
        if (patch.document_id) setHistoryVersion(v => v + 1);
      });

  const verifyGmc = () => {
    const number = (form.gmc_number || "").trim();
    if (!number) return;
    setForm(f => ({ ...f, gmc_last_checked: todayISO() })); // stamped now; persisted only on Save
    setGmc({ loading: true, result: null, error: null });
    axios.get(`${API}/gmc/${encodeURIComponent(number)}`)
      .then(res => setGmc({ loading: false, result: res.data, error: null }))
      .catch(err => setGmc({
        loading: false, result: null,
        error: err.response?.data?.detail || "Could not reach the GMC register.",
      }));
  };

  const verifyHcpc = () => {
    const number = (form.hcpc_number || "").trim();
    if (!number) return;
    setForm(f => ({ ...f, hcpc_last_checked: todayISO() })); // stamped now; persisted only on Save
    setHcpc({ loading: true, result: null, error: null });
    axios.get(`${API}/hcpc/${encodeURIComponent(number)}`)
      .then(res => {
        setHcpc({ loading: false, result: res.data, error: null });
        // The register is the authority for a paramedic's prescribing ("Additional
        // entitlements: IP"), so it sets the tickbox either way. Persisted on Save.
        if (res.data.found && res.data.profcode === "PA") {
          setForm(f => ({ ...f, independent_prescriber: !!res.data.independent_prescriber }));
        }
      })
      .catch(err => setHcpc({
        loading: false, result: null,
        error: err.response?.data?.detail || "Could not reach the HCPC register.",
      }));
  };

  const checkMailchimp = () => {
    const email = (form.email || "").trim();
    if (!email) return;
    setMc({ loading: true, result: null, error: null });
    axios.get(`${API}/mailchimp/lookup`, { params: { email } })
      .then(res => setMc({ loading: false, result: res.data, error: null }))
      .catch(err => setMc({
        loading: false, result: null,
        error: err.response?.data?.detail || "Could not reach MailChimp.",
      }));
  };

  const createMailchimp = () => {
    setMc(s => ({ ...s, loading: true, error: null }));
    axios.post(`${API}/mailchimp/create`, {
      email: (form.email || "").trim(),
      first_name: form.forename,
      last_name: form.surname,
      qualifications: form.qualifications,
      roles: form.roles,
    })
      .then(res => setMc({ loading: false, result: res.data, error: null }))
      .catch(err => setMc(s => ({
        loading: false, result: s.result,
        error: err.response?.data?.detail || "Could not create the MailChimp contact.",
      })));
  };

  // Rewrite an existing member's groups from the form. Sends what's on screen,
  // which may include unsaved edits — the same values "Add to MailChimp" uses.
  const syncMailchimp = () => {
    setMc(s => ({ ...s, loading: true, error: null }));
    axios.post(`${API}/mailchimp/sync`, {
      email: (form.email || "").trim(),
      qualifications: form.qualifications,
      roles: form.roles,
    })
      .then(res => {
        setMc({ loading: false, result: res.data, error: null });
        notify("MailChimp groups updated.", "success");
      })
      .catch(err => setMc(s => ({
        loading: false, result: s.result,
        error: err.response?.data?.detail || "Could not update the MailChimp contact.",
      })));
  };

  // Tenant access: both calls are keyed on the saved record (the server reads the
  // email from it), so they only work on an existing person.
  const checkAccess = () => {
    if (!savedId) return;
    setAccess({ loading: true, result: null, error: null });
    axios.get(`${API}/${savedId}/tenant-access`)
      .then(res => setAccess({ loading: false, result: res.data, error: null }))
      .catch(err => setAccess({
        loading: false, result: null,
        error: err.response?.data?.detail || "Could not check the directory.",
      }));
  };

  const grantAccess = () => {
    if (!savedId) return;
    setAccess(s => ({ ...s, loading: true, error: null }));
    axios.post(`${API}/${savedId}/tenant-access`, { send_invitation_email: inviteEmail })
      .then(res => {
        setAccess({ loading: false, result: res.data, error: null });
        const did = res.data.actions || [];
        notify(did.length ? "Access granted." : "Already had access — nothing to change.",
               did.length ? "success" : "info");
      })
      .catch(err => setAccess(s => ({
        loading: false, result: s.result,
        error: err.response?.data?.detail || "Could not grant access.",
      })));
  };

  const sendSms = () => {
    const phone = (form.phone || "").trim();
    const message = (sms.message || "").trim();
    if (!phone || !message) return;
    setSms(s => ({ ...s, loading: true, error: null }));
    axios.post(`${API}/send-sms`, { phone, message })
      .then(() => {
        notify(`SMS sent to ${form.forename || "contact"}.`, "success");
        setSms(EMPTY_SMS);
      })
      .catch(err => setSms(s => ({
        ...s, loading: false,
        error: err.response?.data?.detail || "Could not send the SMS.",
      })));
  };

  // Blank strings -> null so optional fields stay empty; let the server default added_date.
  const toPayload = () => {
    const out = {};
    for (const [k, v] of Object.entries(form)) {
      out[k] = v === "" ? null : v;
    }
    return out;
  };

  const save = () => runWrite(() => {
    const payload = toPayload();
    const request = isNew
      ? axios.post(API + "/", payload)
      : axios.put(`${API}/${personId}`, payload);
    return request.then(res => { onSaved?.(res.data); close(); });
  });

  const remove = () => {
    axios.delete(`${API}/${personId}`).then(() => {
      setConfirmDelete(false);
      onDeleted?.(personId);
      close();
    });
  };

  // Only the changes on screen: one made since the dialog opened stays pending.
  const markReviewed = () =>
    axios.post(`${API}/${savedId}/changes/review`, { ids: pendingChanges.map(c => c.id) })
      .then(() => { setPendingChanges([]); notify("Changes marked as reviewed.", "success"); });

  // Put a field back to what a change replaced (saved, like any edit, on Save).
  // Documents are left alone: the replaced file is in the document history.
  const restorable = (change) => change.field in BLANK && typeof change.old !== "object";
  const restore = (change) =>
    setForm(f => ({ ...f, [change.field]: change.old ?? BLANK[change.field] }));

  const changed = changedFields(pendingChanges);
  // sx for a field the person changed: a tinted input, merged into any sx it has.
  const mark = (key, baseSx) => (changed.has(key)
    ? { ...(baseSx || {}), "& .MuiOutlinedInput-root": { bgcolor: changedTint } }
    : baseSx);
  // The same for something that isn't an input (a checkbox, an upload).
  const markBox = (key) => (changed.has(key) ? { bgcolor: changedTint, borderRadius: 1, px: 1 } : undefined);
  const regChanged = ["gmc_number", "nmc_pin", "hcpc_number"].some(k => changed.has(k));

  const savePermRoles = () => {
    if (!savedId) return;
    axios.put(`${API}/${savedId}/permission-roles`, { permission_roles: permRoles })
      .then(res => {
        notify("Permission roles updated.", "success");
        setInitialPermRoles(permRoles); // saved, so no longer an unsaved change
        onSaved?.(res.data);
      });
  };

  // The address sign-in access is checked and granted for — the server reads it
  // off the saved record the same way (core/identity.login_address).
  const loginAddress = (form.login_email || "").trim() || (form.email || "").trim();

  // Choosing a grade dates it today. A doctor in training gives their educational
  // supervisor as referee, so that's prefilled when the position is blank — and
  // taken back off if the grade becomes a register while it's still the default.
  const setGrade = (grade) => setForm(f => {
    const position = (f.reference_position || "").trim();
    let reference_position = f.reference_position;
    if (isTrainingGrade(grade) && !position) reference_position = TRAINING_REFERENCE_POSITION;
    if (grade && !isTrainingGrade(grade) && position === TRAINING_REFERENCE_POSITION) reference_position = "";
    return {
      ...f, doctor_grade: grade, reference_position,
      doctor_grade_date: grade ? (grade === f.doctor_grade ? f.doctor_grade_date : todayISO()) : "",
    };
  });

  const field = (key) => ({
    value: form[key],
    onChange: e => setForm({ ...form, [key]: e.target.value }),
    ...(changed.has(key) ? { sx: mark(key) } : {}),
  });

  // --- Registration-number fields shown conditionally on qualifications ---
  const qualHas = (q) => form.qualifications.includes(q);
  const hcpcPrefixes = [qualHas("Paramedic") && "PA", qualHas("Physiotherapist") && "PH"].filter(Boolean);
  const HCPC_RE = hcpcRegex(form.qualifications);
  // Validate the raw value (no trim) so stray whitespace is caught, not silently accepted.
  const badFormat = (val, re) => val !== "" && !re.test(val);

  const showGmc = GMC_QUALIFICATIONS.some(qualHas);
  // A physician associate has a GMC number but no grade.
  const showGrade = qualHas("Doctor") || qualHas("Doctor (training)");
  const showNmc = qualHas("Nurse");
  const showHcpc = qualHas("Paramedic") || qualHas("Physiotherapist");
  const showPrescriber = showNmc || qualHas("Paramedic");
  // Why the GMC record doesn't bear out the chosen grade (server-judged on Verify).
  const gradeProblems = (showGrade && form.doctor_grade && gmc.result?.grade_problems?.[form.doctor_grade]) || [];
  const regInvalid =
    (showGmc && badFormat(form.gmc_number, GMC_RE)) ||
    (showNmc && badFormat(form.nmc_pin, NMC_RE)) ||
    (showHcpc && badFormat(form.hcpc_number, HCPC_RE));
  // Healthcare professionals need their own indemnity; first aiders are covered by the group policy.
  const isHcp = (form.qualifications || []).some(q => HCP_QUALIFICATIONS.includes(q));

  // Indemnity: when a provider is set, expiry + certificate (and "Other" text) are required.
  // Only enforced for HCPs — the indemnity fields are hidden for everyone else.
  const indemnityActive = form.indemnity_provider && form.indemnity_provider !== "None";
  const indemnityOk = !isHcp || !indemnityActive || (
    (form.indemnity_provider !== "Other" || form.indemnity_provider_other.trim()) &&
    form.indemnity_expiry &&
    form.indemnity_certificate_id
  );

  // MRT Team is mandatory when RRMT is held.
  const mrtOk = !qualHas("RRMT") || form.mrt_team.trim();
  const certQuals = CERT_QUALIFICATIONS.filter(q => form.qualifications.includes(q));

  // Qualifications that build on others (WMCI needs ML, WML and MCI). Reported
  // together as one sentence per unsatisfied award, so ticking WMCI on its own
  // names all three rather than one at a time. The server rejects it too.
  const qualPrereqIssues = Object.entries(QUALIFICATION_PREREQUISITES)
    .filter(([q]) => qualHas(q))
    .map(([q, needed]) => [q, needed.filter(n => !qualHas(n))])
    .filter(([, missing]) => missing.length > 0)
    .map(([q, missing]) => `${q} requires ${missing.join(", ")}.`);

  // Block saving while a shown registration number is present but invalid, or
  // indemnity/MRT/qualification prerequisites incomplete.
  const canSave = form.forename.trim() && form.surname.trim() && !regInvalid
    && indemnityOk && mrtOk && qualPrereqIssues.length === 0;

  // Info "i" tooltip rendered as an end adornment on a field.
  const infoSlot = (tip) => ({
    input: {
      endAdornment: (
        <InputAdornment position="end">
          <Tooltip title={tip} arrow>
            <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: "help" }} />
          </Tooltip>
        </InputAdornment>
      ),
    },
  });

  const openInNew = (url) => window.open(url, "_blank", "noopener");

  const chipsOrDash = (list) => (list && list.length > 0)
    ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {list.map((l, i) => <Chip key={i} label={l} size="small" />)}
      </Box>
    )
    : "—";

  // --- MailChimp: what a rewrite would set, and whether it differs ---
  // Only groups the audience actually offers can differ: PERSONNEL_ROLES holds
  // values MailChimp has no group for (Team Lead, Medical 2IC, its own "Race
  // Control" vs our "Event Control") and "Other" likewise, and there is nothing
  // to write those to. `available` comes from the lookup, so the server stays
  // the authority on the mapping; this only decides whether to offer the button.
  // Recomputed each render, so editing a role after checking updates the answer
  // instead of leaving a stale one on screen.
  const mcTarget = (() => {
    const av = mc.result?.found ? mc.result.available : null;
    if (!av) return null;
    const wanted = (held, offered) => (held || []).filter(v => offered.includes(v)).sort();
    const sorted = (list) => [...(list || [])].sort();
    const same = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
    const roles = wanted(form.roles, av.roles);
    const qualifications = wanted(form.qualifications, av.qualifications);
    return {
      roles,
      qualifications,
      differs: !same(roles, sorted(mc.result.roles))
        || !same(qualifications, sorted(mc.result.qualifications)),
    };
  })();

  // --- Compliance highlighting for the person being edited ---
  // Red where a future event would fail the check; amber where the gap exists but no future event.
  // Only the attendances the Compliance page itself checks count as a future event
  // here, or the record would go red over an event nobody is asked about.
  const today = todayISO();
  const myFutureEvents = attendances
    .filter(a => COMPLIANCE_CHECKED_STATUSES.includes(a.status))
    .map(a => a.event)
    .filter(ev => ev && ev.end_date && ev.end_date >= today);
  const hasFuture = myFutureEvents.length > 0;
  const maxFutureEnd = myFutureEvents.reduce((m, e) => (e.end_date > m ? e.end_date : m), "");
  const maxFutureStart = myFutureEvents.reduce((m, e) => ((e.start_date || "") > m ? (e.start_date || "") : m), "");

  const presenceComp = (gap, errMsg, warnMsg) =>
    gap ? { sev: hasFuture ? "error" : "warning", msg: hasFuture ? errMsg : warnMsg } : null;
  const expiryComp = (expiry) => {
    if (!expiry) return null;
    if (hasFuture && expiry < maxFutureEnd) return { sev: "error", msg: "Expires before an upcoming event" };
    if (!hasFuture && expiry < today) return { sev: "warning", msg: "Expired" };
    return null;
  };
  const lastCheckedComp = (lc) => {
    if (!isHcp) return null;
    if (!lc) return { sev: hasFuture ? "error" : "warning", msg: "ID not checked" };
    if (hasFuture && lc < oneYearBeforeISO(maxFutureStart)) return { sev: "error", msg: "Not checked within a year of an upcoming event" };
    if (!hasFuture && lc < oneYearBeforeISO(today)) return { sev: "warning", msg: "Last checked over a year ago" };
    return null;
  };
  // Spreadable TextField props for a {sev,msg} result (merges a base sx).
  const compProps = (comp, baseSx) => {
    if (!comp) return baseSx ? { sx: baseSx } : {};
    if (comp.sev === "error") return { error: true, helperText: comp.msg, sx: baseSx };
    return {
      helperText: comp.msg,
      sx: {
        ...(baseSx || {}),
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main" },
        "& .MuiFormHelperText-root": { color: "warning.main" },
      },
    };
  };

  const provComp = presenceComp(isHcp && (!form.indemnity_provider || form.indemnity_provider === "None"),
                                "No indemnity for an upcoming event", "Indemnity provider not set");
  const indCertComp = presenceComp(indemnityActive && !form.indemnity_certificate_id,
                                   "Certificate required for an upcoming event", "Certificate not uploaded");
  const indExpComp = expiryComp(form.indemnity_expiry);

  return (
    <>
      <Dialog open={open} onClose={requestClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
        {/* While the record is in flight `form` still holds whoever was open last,
            so the name is only shown once it's this person's. */}
        <DialogTitle>
          {isNew ? "Add personnel"
            : loading ? "Personnel record"
            : `Edit ${form.forename} ${form.surname}`}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {pendingChanges.length > 0 && (
              <Alert severity="info" icon={<RateReviewIcon />}
                     action={
                       <Button color="inherit" size="small" onClick={markReviewed} sx={{ whiteSpace: "nowrap" }}>
                         Mark as reviewed
                       </Button>
                     }>
                <AlertTitle>Changed by {form.forename || "them"} in My details — awaiting review</AlertTitle>
                {pendingChanges.map(c => (
                  <Box key={c.id} sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {fmtDateTime(c.changed_at)}{c.changed_by ? ` · ${c.changed_by}` : ""}
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {c.changes.map(x => (
                        <Typography component="li" variant="body2" key={x.field}>
                          {changeLabel(x.field)}: {changeValue(x.old)} → <strong>{changeValue(x.new)}</strong>
                          {canEdit && restorable(x) && (
                            <>
                              {" "}
                              <Link component="button" type="button" variant="body2" onClick={() => restore(x)}>
                                Restore previous
                              </Link>
                            </>
                          )}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
                <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                  These are already saved on the record and highlighted below. Marking them
                  reviewed clears this notice; Restore previous puts a value back on Save.
                  {regChanged && " A registration number changed — check it against the register again."}
                </Typography>
              </Alert>
            )}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Forename" fullWidth required {...field("forename")} />
              <TextField label="Surname" fullWidth required {...field("surname")} />
              <TextField label="Status" select {...field("status")}
                         sx={{ width: 220, flexShrink: 0 }}
                         helperText={PERSONNEL_STATUSES.find(s => s.value === form.status)?.hint}>
                {PERSONNEL_STATUSES.map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.value}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Email" fullWidth value={form.email}
                         onChange={e => { setForm({ ...form, email: e.target.value }); setMc(EMPTY_VERIFY); }} />
              <TextField label="Phone" fullWidth {...field("phone")} />
              <TextField label="Date of birth" type="date"
                         slotProps={{ inputLabel: { shrink: true } }} {...field("date_of_birth")}
                         sx={mark("date_of_birth", { width: 190, flexShrink: 0 })} />
            </Box>
            {/* The Microsoft account they sign in with, when it isn't the email
                above. Blank (or the same address) means the email above signs in. */}
            {loginOpen ? (
              <TextField label="Sign-in address, if different" fullWidth {...field("login_email")}
                         helperText="The Microsoft account they sign in with, used for sign-in access and to link them to their record. Leave blank to sign in with the email above." />
            ) : (
              <Box>
                <Link component="button" type="button" variant="body2" onClick={() => setLoginOpen(true)}>
                  Signs in with a different address?
                </Link>
              </Box>
            )}

            {/* MailChimp audience sync */}
            <Box sx={{ display: "grid", gap: 1 }}>
              {canActions && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button variant="outlined" startIcon={mc.loading ? <CircularProgress size={16} /> : <CampaignIcon />}
                          disabled={!(form.email || "").includes("@") || mc.loading}
                          onClick={checkMailchimp}>
                    Check MailChimp
                  </Button>
                  <Button variant="outlined" startIcon={<SmsIcon />}
                          disabled={!(form.phone || "").trim()}
                          onClick={() => setSms({ ...EMPTY_SMS, open: true })}>
                    Send message
                  </Button>
                </Box>
              )}
              {mc.error && <Alert severity="error">{mc.error}</Alert>}
              {mc.result && !mc.result.found && (
                <Alert severity="info"
                       action={
                         <Button color="inherit" size="small" disabled={mc.loading} onClick={createMailchimp}>
                           Add to MailChimp
                         </Button>
                       }>
                  Not in the MailChimp audience. Adding will subscribe them and set their
                  Roles and Qualifications groups.
                </Alert>
              )}
              {mc.result && mc.result.found && (
                <Alert severity={mcTarget?.differs ? "warning" : "success"} icon={<CampaignIcon />}>
                  <AlertTitle>In the MailChimp audience</AlertTitle>
                  <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5 }}>
                    <Box component="dt" sx={{ fontWeight: 600 }}>Status</Box>
                    <Box component="dd" sx={{ m: 0 }}>{mc.result.status}</Box>
                    {mc.result.created && (
                      <>
                        <Box component="dt" sx={{ fontWeight: 600 }}>Created</Box>
                        <Box component="dd" sx={{ m: 0 }}>{new Date(mc.result.created).toLocaleDateString()}</Box>
                      </>
                    )}
                    <Box component="dt" sx={{ fontWeight: 600 }}>Roles</Box>
                    <Box component="dd" sx={{ m: 0 }}>{chipsOrDash(mc.result.roles)}</Box>
                    <Box component="dt" sx={{ fontWeight: 600 }}>Qualifications</Box>
                    <Box component="dd" sx={{ m: 0 }}>{chipsOrDash(mc.result.qualifications)}</Box>
                    <Box component="dt" sx={{ fontWeight: 600 }}>Tags</Box>
                    <Box component="dd" sx={{ m: 0 }}>{chipsOrDash(mc.result.tags)}</Box>
                  </Box>
                  {mcTarget?.differs && (
                    <Box sx={{ mt: 1.5, display: "grid", gap: 0.5, justifyItems: "start" }}>
                      <Typography variant="body2">
                        This doesn't match the record. Updating will set Roles to{" "}
                        <strong>{mcTarget.roles.join(", ") || "none"}</strong> and Qualifications to{" "}
                        <strong>{mcTarget.qualifications.join(", ") || "none"}</strong>.
                      </Typography>
                      <Button size="small" variant="outlined" color="inherit" disabled={mc.loading}
                              startIcon={mc.loading ? <CircularProgress size={16} /> : <SyncIcon />}
                              onClick={syncMailchimp}>
                        Update MailChimp
                      </Button>
                    </Box>
                  )}
                  {mc.result.skipped_qualifications?.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      Not set in MailChimp (no matching group option): {mc.result.skipped_qualifications.join(", ")}
                    </Typography>
                  )}
                  {mc.result.admin_url && (
                    <Link href={mc.result.admin_url} target="_blank" rel="noopener"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 1, fontSize: 13 }}>
                      Edit in MailChimp <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  )}
                </Alert>
              )}
            </Box>

            {/* Shelter Stone roles (multi-select) */}
            <TextField
              label="Shelter Stone role"
              select
              fullWidth
              value={form.roles}
              onChange={e => {
                const value = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                setForm(f => ({ ...f, roles: value }));
              }}
              slotProps={{
                select: {
                  multiple: true,
                  renderValue: (selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map(v => <Chip key={v} label={v} size="small" />)}
                    </Box>
                  ),
                },
              }}
            >
              {PERSONNEL_ROLES.map(r => (
                <MenuItem key={r} value={r}>
                  <Checkbox checked={form.roles.indexOf(r) > -1} />
                  <ListItemText primary={r} />
                </MenuItem>
              ))}
            </TextField>

            {/* Qualifications (multi-select) */}
            <TextField
              label="Qualifications"
              select
              fullWidth
              error={qualPrereqIssues.length > 0}
              helperText={qualPrereqIssues.join(" ") || " "}
              value={form.qualifications}
              onChange={e => {
                const value = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                setForm(f => ({
                  ...f,
                  qualifications: value,
                  // drop the free text if "Other" was just deselected
                  qualifications_other: value.includes("Other") ? f.qualifications_other : "",
                }));
              }}
              slotProps={{
                select: {
                  multiple: true,
                  renderValue: (selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map(v => <Chip key={v} label={v} size="small" />)}
                    </Box>
                  ),
                },
              }}
            >
              {[...PERSONNEL_QUALIFICATIONS,
                ...RETIRED_QUALIFICATIONS.filter(q => form.qualifications.includes(q))].map(q => (
                <MenuItem key={q} value={q}>
                  <Checkbox checked={form.qualifications.indexOf(q) > -1} />
                  <ListItemText primary={q} />
                </MenuItem>
              ))}
            </TextField>
            {form.qualifications.includes("Other") && (
              <TextField label="Other qualification" fullWidth
                         helperText="Specify the 'Other' qualification"
                         {...field("qualifications_other")} />
            )}

            {/* GMC — doctors and physician associates. Verified in-app against the GMC register. */}
            {showGmc && (
              <Box sx={{ display: "grid", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <TextField label="GMC number" fullWidth sx={mark("gmc_number")}
                             value={form.gmc_number}
                             error={badFormat(form.gmc_number, GMC_RE)}
                             helperText={badFormat(form.gmc_number, GMC_RE) ? "7 digits, optionally prefixed with 'A'." : " "}
                             onChange={e => { setForm({ ...form, gmc_number: regInput(e.target.value) }); resetVerify(); }}
                             slotProps={infoSlot("General Medical Council number for doctors and physician associates. Format: 7 digits, optionally prefixed with 'A' (e.g. 7456724).")} />
                  <TextField label="Last checked" type="date"
                             slotProps={{ inputLabel: { shrink: true } }}
                             value={form.gmc_last_checked}
                             onChange={e => setForm({ ...form, gmc_last_checked: e.target.value })}
                             {...compProps(lastCheckedComp(form.gmc_last_checked), { width: 190, flexShrink: 0 })} />
                  {canActions && (
                    <Button variant="outlined" onClick={verifyGmc}
                            disabled={!form.gmc_number || badFormat(form.gmc_number, GMC_RE) || gmc.loading}
                            startIcon={gmc.loading ? <CircularProgress size={16} /> : <VerifiedIcon />}
                            sx={{ mt: 0.5, whiteSpace: "nowrap" }}>
                      Verify
                    </Button>
                  )}
                </Box>
                {showGrade && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <TextField label="Grade" select fullWidth sx={mark("doctor_grade")}
                             value={form.doctor_grade}
                             onChange={e => setGrade(e.target.value)}
                             helperText={gradeProblems.length ? "Doesn't match the GMC register — see below." : " "}
                             error={gradeProblems.length > 0}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {DOCTOR_GRADES.map(g => (
                      <MenuItem key={g} value={g}>
                        {g === SAS_DOCTOR ? "SAS Doctor (Specialty / Specialist / Assoc. Specialist)" : g}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Grade set" type="date"
                             slotProps={{ inputLabel: { shrink: true } }}
                             disabled={!form.doctor_grade}
                             value={form.doctor_grade_date}
                             onChange={e => setForm({ ...form, doctor_grade_date: e.target.value })}
                             sx={{ width: 190, flexShrink: 0 }} />
                </Box>
                )}
                {gmc.error && <Alert severity="error">{gmc.error}</Alert>}
                {gmc.result && !gmc.result.found && (
                  <Alert severity="warning">
                    No registrant found for GMC number {gmc.result.gmc_number}.
                    {gmc.result.error ? ` ${gmc.result.error}` : ""}
                  </Alert>
                )}
                {gmc.result && gmc.result.found && (
                  <Alert severity="success" icon={<VerifiedIcon />}>
                    <AlertTitle>Found on the GMC register</AlertTitle>
                    <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5 }}>
                      {[
                        ["Name", gmc.result.name],
                        ["GMC number", gmc.result.gmc_number],
                        ["Profession", gmc.result.profession],
                        ["Gender", gmc.result.gender],
                        ["Registration status", gmc.result.registration_status],
                        ["GP register", gmc.result.gp_register],
                        ["Specialist register", gmc.result.specialist_register],
                        ["Provisional registration", gmc.result.provisional_registration_date],
                        ["Full registration", gmc.result.full_registration_date],
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <Box key={label} sx={{ display: "contents" }}>
                          <Box component="dt" sx={{ fontWeight: 600 }}>{label}</Box>
                          <Box component="dd" sx={{ m: 0 }}>{value}</Box>
                        </Box>
                      ))}
                    </Box>
                    <Link href={gmc.result.source_url} target="_blank" rel="noopener" sx={{ display: "inline-block", mt: 1, fontSize: 13 }}>
                      View on gmc-uk.org
                    </Link>
                  </Alert>
                )}
                {/* Lower bounds only: someone further on than their grade has most
                    likely just not updated it, so that is never flagged. */}
                {gradeProblems.length > 0 && (
                  <Alert severity="warning">
                    <AlertTitle>Grade {form.doctor_grade} doesn't look right for this record</AlertTitle>
                    {gradeProblems.map(p => <div key={p}>{p}</div>)}
                  </Alert>
                )}
              </Box>
            )}

            {/* NMC — nurses. CAPTCHA-protected, so we deep-link to the NMC search. */}
            {showNmc && (
              <Box sx={{ display: "grid", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <TextField label="NMC PIN" fullWidth sx={mark("nmc_pin")}
                             value={form.nmc_pin}
                             error={badFormat(form.nmc_pin, NMC_RE)}
                             helperText={badFormat(form.nmc_pin, NMC_RE)
                               ? "8 characters: digits and CAPITAL letters (e.g. 07B2988E)."
                               : "The NMC register is CAPTCHA-protected — use the button to check it."}
                             onChange={e => setForm({ ...form, nmc_pin: regInput(e.target.value) })}
                             slotProps={infoSlot("Nursing and Midwifery Council PIN for nurses. Format: 8 alphanumeric characters, letters in capitals (e.g. 07B2988E).")} />
                  <TextField label="Last checked" type="date"
                             slotProps={{ inputLabel: { shrink: true } }}
                             value={form.nmc_last_checked}
                             onChange={e => setForm({ ...form, nmc_last_checked: e.target.value })}
                             {...compProps(lastCheckedComp(form.nmc_last_checked), { width: 190, flexShrink: 0 })} />
                  <Button variant="outlined" startIcon={<OpenInNewIcon />}
                          onClick={() => { setForm(f => ({ ...f, nmc_last_checked: todayISO() })); openInNew("https://www.nmc.org.uk/registration/search-the-register/"); }}
                          sx={{ mt: 0.5, whiteSpace: "nowrap" }}>
                    Search NMC
                  </Button>
                </Box>
              </Box>
            )}

            {/* HCPC — paramedics / physiotherapists. Verified in-app via the register API;
                the deep-link to the public (CAPTCHA-protected) record is retained too. */}
            {showHcpc && (
              <Box sx={{ display: "grid", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <TextField label="HCPC number" fullWidth sx={mark("hcpc_number")}
                             value={form.hcpc_number}
                             error={badFormat(form.hcpc_number, HCPC_RE)}
                             helperText={badFormat(form.hcpc_number, HCPC_RE)
                               ? `${(hcpcPrefixes.length ? hcpcPrefixes : ["PA", "PH"]).join(" or ")} followed by 5 or 6 digits.`
                               : " "}
                             onChange={e => { setForm({ ...form, hcpc_number: regInput(e.target.value) }); resetVerify(); }}
                             slotProps={infoSlot("Health & Care Professions Council number. Two letters then 5–6 digits — PA for paramedics, PH for physiotherapists (e.g. PA051451).")} />
                  <TextField label="Last checked" type="date"
                             slotProps={{ inputLabel: { shrink: true } }}
                             value={form.hcpc_last_checked}
                             onChange={e => setForm({ ...form, hcpc_last_checked: e.target.value })}
                             {...compProps(lastCheckedComp(form.hcpc_last_checked), { width: 190, flexShrink: 0 })} />
                  {canActions && (
                    <Button variant="outlined" onClick={verifyHcpc}
                            disabled={!form.hcpc_number || badFormat(form.hcpc_number, HCPC_RE) || hcpc.loading}
                            startIcon={hcpc.loading ? <CircularProgress size={16} /> : <VerifiedIcon />}
                            sx={{ mt: 0.5, whiteSpace: "nowrap" }}>
                      Verify
                    </Button>
                  )}
                  <Button variant="outlined" startIcon={<OpenInNewIcon />}
                          disabled={!form.hcpc_number || badFormat(form.hcpc_number, HCPC_RE)}
                          onClick={() => { setForm(f => ({ ...f, hcpc_last_checked: todayISO() })); openInNew(`https://www.hcpc-uk.org/check-the-register/professional-registration-detail/?query=${encodeURIComponent(form.hcpc_number)}&profession=${form.hcpc_number.slice(0, 2)}`); }}
                          sx={{ mt: 0.5, whiteSpace: "nowrap" }}>
                    Open record
                  </Button>
                </Box>
                {hcpc.error && <Alert severity="error">{hcpc.error}</Alert>}
                {hcpc.result && !hcpc.result.found && (
                  <Alert severity="warning">
                    No registrant found for HCPC number {hcpc.result.hcpc_number}.
                    {hcpc.result.error ? ` ${hcpc.result.error}` : ""}
                  </Alert>
                )}
                {hcpc.result && hcpc.result.found && (
                  <Alert severity="success" icon={<VerifiedIcon />}>
                    <AlertTitle>Found on the HCPC register</AlertTitle>
                    <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5 }}>
                      {[
                        ["Name", hcpc.result.name],
                        ["HCPC number", hcpc.result.hcpc_number],
                        ["Profession", hcpc.result.profession],
                        ["Town", hcpc.result.town],
                        ["Registration status", hcpc.result.status],
                        ["Registered from", hcpc.result.registered_from],
                        ["Registration expiry", hcpc.result.registered_to],
                        ["Additional entitlements",
                         [hcpc.result.independent_prescriber && "Independent prescriber (IP)",
                          hcpc.result.supplementary_prescriber && "Supplementary prescriber (SP)"]
                           .filter(Boolean).join(", ") || "None"],
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <Box key={label} sx={{ display: "contents" }}>
                          <Box component="dt" sx={{ fontWeight: 600 }}>{label}</Box>
                          <Box component="dd" sx={{ m: 0 }}>{value}</Box>
                        </Box>
                      ))}
                    </Box>
                    <Link href={hcpc.result.source_url} target="_blank" rel="noopener" sx={{ display: "inline-block", mt: 1, fontSize: 13 }}>
                      View on hcpc-uk.org
                    </Link>
                  </Alert>
                )}
              </Box>
            )}

            {/* Nurses and paramedics. A paramedic's is set from the HCPC register on
                Verify; the NMC has no API, so a nurse's is checked by hand. */}
            {showPrescriber && (
              <Box sx={markBox("independent_prescriber")}>
                <FormControlLabel
                  control={<Checkbox checked={form.independent_prescriber}
                                     onChange={e => setForm({ ...form, independent_prescriber: e.target.checked })} />}
                  label="Independent prescriber" />
                <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 4, mt: -0.5 }}>
                  {qualHas("Paramedic")
                    ? "Set from the HCPC register's additional entitlements (IP) on Verify."
                    : "Check the NMC register by hand — it has no API."}
                </Typography>
              </Box>
            )}

            {/* MRT team — required when RRMT is held */}
            {qualHas("RRMT") && (
              <TextField label="MRT Team" fullWidth required value={form.mrt_team} sx={mark("mrt_team")}
                         error={!form.mrt_team.trim()}
                         helperText={!form.mrt_team.trim() ? "Required for RRMT" : " "}
                         onChange={e => setForm({ ...form, mrt_team: e.target.value })} />
            )}

            {/* Per-qualification certificates (RRMT/FREC3/FREC4/WEMT/ML/WML/MCI/WMCI), each optional */}
            {certQuals.length > 0 && (
              <>
                <Divider textAlign="left">Qualification certificates</Divider>
                {isNew ? (
                  <Typography variant="body2" color="text.secondary">
                    Save this person first, then re-open to upload qualification certificates.
                  </Typography>
                ) : (
                  certQuals.map(q => {
                    const row = qualCerts.find(qc => qc.qualification === q);
                    // Saved value until the box is touched, then whatever is in it.
                    const expiry = certExpiry[q] ?? (row?.expiry || "");
                    const partialExpiry = expiry && !isCompleteDate(expiry);
                    return (
                      <Box key={q} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, display: "grid", gap: 1.5,
                                         ...(changed.has(certificateField(q)) ? { bgcolor: changedTint } : {}) }}>
                        <Typography variant="subtitle2">{q}</Typography>
                        <DocumentUpload
                          doc={row?.document || null} label={`${q} certificate`} category="qualification_certificate"
                          personnelId={savedId}
                          severity={presenceComp(!row || !row.document_id,
                                                 "Required for an upcoming event", "Certificate not uploaded")?.sev}
                          onUploaded={d => upsertQualCert(q, { document_id: d.id })}
                          onDeleted={() => refreshQualCerts(savedId)} />
                        {/* Expiry only for the certificates that have one — the mountain
                            qualifications are held for life, so asking would invite a guess. */}
                        {EXPIRY_QUALIFICATIONS.includes(q) && (
                          <TextField label="Expiry" type="date" size="small"
                                     slotProps={{ inputLabel: { shrink: true } }}
                                     value={expiry}
                                     onChange={e => {
                                       const v = e.target.value;
                                       setCertExpiry(d => ({ ...d, [q]: v }));
                                       // Cleared, or finished: a half-typed year isn't a date.
                                       if (!v || isCompleteDate(v)) upsertQualCert(q, { expiry: v || null });
                                     }}
                                     {...(partialExpiry
                                       ? { error: true, helperText: "Enter a 4-digit year", sx: { maxWidth: 220 } }
                                       : compProps(expiryComp(expiry), mark(certificateExpiryField(q), { maxWidth: 220 })))} />
                        )}
                      </Box>
                    );
                  })
                )}
              </>
            )}

            {/* Driving licence — the card itself rather than a certificate, so it
                has its own fields on the record instead of a qualification-
                certificate row. Everything here is optional: the licence is often
                known long before anyone has photographed both sides of it. */}
            {qualHas(DRIVING_LICENCE_QUALIFICATION) && (
              <>
                <Divider textAlign="left">Driving licence</Divider>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <TextField label="Licence number" fullWidth sx={mark("driving_licence_number")}
                             value={form.driving_licence_number}
                             onChange={e => setForm({ ...form, driving_licence_number: regInput(e.target.value) })} />
                  <TextField label="Test date" type="date"
                             slotProps={{ inputLabel: { shrink: true } }}
                             {...field("driving_licence_test_date")}
                             sx={mark("driving_licence_test_date", { width: 190, flexShrink: 0 })} />
                  <TextField label="Expiry" type="date"
                             slotProps={{ inputLabel: { shrink: true } }}
                             value={form.driving_licence_expiry}
                             onChange={e => setForm({ ...form, driving_licence_expiry: e.target.value })}
                             {...compProps(expiryComp(form.driving_licence_expiry), mark("driving_licence_expiry", { width: 190, flexShrink: 0 }))} />
                </Box>
<Box sx={markBox("driving_licence_front_id")}>
                <DocumentUpload
                  doc={licenceFront} label="licence photo (front)" category="driving_licence_front"
                  personnelId={savedId}
                  onUploaded={d => { setLicenceFront(d); setForm(f => ({ ...f, driving_licence_front_id: d.id })); }}
                  onDeleted={() => { setLicenceFront(null); setForm(f => ({ ...f, driving_licence_front_id: null })); }} />
                </Box>
<Box sx={markBox("driving_licence_back_id")}>
                <DocumentUpload
                  doc={licenceBack} label="licence photo (back)" category="driving_licence_back"
                  personnelId={savedId}
                  onUploaded={d => { setLicenceBack(d); setForm(f => ({ ...f, driving_licence_back_id: d.id })); }}
                  onDeleted={() => { setLicenceBack(null); setForm(f => ({ ...f, driving_licence_back_id: null })); }} />
                </Box>
              </>
            )}

            {/* Indemnity */}
            <Divider textAlign="left">Indemnity</Divider>
            {!isHcp ? (
              <Typography variant="body2" color="text.secondary">
                First aiders are covered by the Shelter Stone group indemnity policy.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <TextField label="Indemnity provider" select fullWidth value={form.indemnity_provider}
                             {...compProps(provComp, mark("indemnity_provider"))}
                             onChange={e => setForm(f => ({
                               ...f,
                               indemnity_provider: e.target.value,
                               indemnity_provider_other: e.target.value === "Other" ? f.indemnity_provider_other : "",
                             }))}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {INDEMNITY_PROVIDERS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </TextField>
                  {indemnityActive && (
                    <TextField label="Indemnity expiry" type="date" required
                               slotProps={{ inputLabel: { shrink: true } }}
                               error={!form.indemnity_expiry || indExpComp?.sev === "error"}
                               helperText={!form.indemnity_expiry ? "Required" : (indExpComp?.msg || " ")}
                               sx={mark("indemnity_expiry", { width: 220, flexShrink: 0, ...(indExpComp?.sev === "warning" ? {
                                 "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main" },
                                 "& .MuiFormHelperText-root": { color: "warning.main" },
                               } : {}) })}
                               value={form.indemnity_expiry}
                               onChange={e => setForm({ ...form, indemnity_expiry: e.target.value })} />
                  )}
                </Box>
                {form.indemnity_provider === "Other" && (
                  <TextField label="Provider name" fullWidth required value={form.indemnity_provider_other}
                           sx={mark("indemnity_provider_other")}
                             error={!form.indemnity_provider_other.trim()}
                             onChange={e => setForm({ ...form, indemnity_provider_other: e.target.value })} />
                )}
                {indemnityActive && (
<Box sx={markBox("indemnity_certificate_id")}>
                  <DocumentUpload
                    doc={cert} label="certificate" category="indemnity_certificate"
                    severity={indCertComp?.sev}
                    personnelId={savedId}
                    onUploaded={d => { setCert(d); setForm(f => ({ ...f, indemnity_certificate_id: d.id })); }}
                    onDeleted={() => { setCert(null); setForm(f => ({ ...f, indemnity_certificate_id: null })); }} />
                </Box>
                )}
              </>
            )}

            {/* CV */}
            <Divider textAlign="left">CV</Divider>
<Box sx={markBox("cv_document_id")}>
            <DocumentUpload
              doc={cvDoc} label="CV" category="cv"
              personnelId={savedId}
              onUploaded={d => { setCvDoc(d); setForm(f => ({ ...f, cv_document_id: d.id, cv_uploaded_date: f.cv_uploaded_date || todayISO() })); }}
              onDeleted={() => { setCvDoc(null); setForm(f => ({ ...f, cv_document_id: null, cv_uploaded_date: "" })); }} />
                </Box>
            {cvDoc && (
              <TextField label="CV uploaded date" type="date" fullWidth
                         slotProps={{ inputLabel: { shrink: true } }}
                         value={form.cv_uploaded_date}
                         onChange={e => setForm({ ...form, cv_uploaded_date: e.target.value })} />
            )}
            {/* Someone well known to us: compliance won't flag a missing CV. */}
            <FormControlLabel
              control={<Checkbox checked={form.cv_not_required}
                                 onChange={e => setForm({ ...form, cv_not_required: e.target.checked })} />}
              label="CV not required (well known to us)" />
            {/* The referee sits with the CV: both are what we know of someone's background. */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Reference name" fullWidth {...field("reference_name")} />
              <TextField label="Reference position" fullWidth {...field("reference_position")} />
              <TextField label="Reference email" type="email" fullWidth {...field("reference_email")} />
            </Box>

            {/* Other details */}
            <Divider textAlign="left">Other details</Divider>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Emergency contact name" fullWidth {...field("emergency_contact_name")} />
              <TextField label="Emergency contact phone" fullWidth {...field("emergency_contact_phone")} />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Clothing size" select fullWidth {...field("clothing_size")}>
                <MenuItem value=""><em>—</em></MenuItem>
                {CLOTHING_SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <TextField label="Vehicle" select fullWidth {...field("vehicle")}>
                <MenuItem value=""><em>—</em></MenuItem>
                {VEHICLES.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
              {/* Uppercased but not stripped of spaces, unlike the registration
                  numbers above: a number plate is normally written with one. */}
              <TextField label="Vehicle registration" fullWidth sx={mark("vehicle_registration")}
                         value={form.vehicle_registration}
                         onChange={e => setForm({ ...form, vehicle_registration: e.target.value.toUpperCase() })} />
            </Box>
            <TextField label="Address line 1" fullWidth {...field("address_line1")} />
            <TextField label="Address line 2" fullWidth {...field("address_line2")} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Town" fullWidth {...field("town")} />
              <TextField label="County" fullWidth {...field("county")} />
              <TextField label="Postcode" fullWidth {...field("postcode")} />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Added date" type="date" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                         helperText="Defaults to today if left blank" {...field("added_date")} />
              <TextField label="Source" fullWidth helperText="Where their details came from"
                         {...field("source")} />
            </Box>
            {canViewPrivate && (
              <TextField label="Private notes" fullWidth multiline minRows={2} {...field("private_notes")} />
            )}

            {/* Declarations of suitability — made by the person on My details, so
                read-only here. Needed before they can say they're available. */}
            {!isNew && (
              <>
                <Divider textAlign="left">Declarations of suitability</Divider>
                <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" }, columnGap: 2, rowGap: 0.5 }}>
                  {DECLARATIONS.map(d => (
                    <Box key={d.field} sx={{ display: "contents" }}>
                      <Box component="dt" sx={{ fontWeight: 600, color: "text.secondary" }}>
                        <Tooltip title={d.text} arrow><span>{d.title}</span></Tooltip>
                      </Box>
                      <Box component="dd" sx={{ m: 0, ...(markBox(d.field) || {}) }}>
                        {declarations[d.field]
                          ? `Confirmed ${fmtDateTime(declarations[d.field])}`
                          : <Typography component="span" variant="body2" color="text.secondary">Not yet confirmed</Typography>}
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Only the person can make these, on My details. Hover a title for its wording.
                </Typography>
              </>
            )}

            {/* Permission roles — Super Admin only, existing records only. */}
            {canEditPermissions && !isNew && (
              <>
                <Divider textAlign="left">Permissions (Super Admin)</Divider>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <TextField select label="Global roles" sx={{ flex: 1 }}
                             value={permRoles}
                             onChange={e => setPermRoles(
                               typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                             helperText="One person can hold several (e.g. Events Manager + Inventory Manager)"
                             slotProps={{
                               select: {
                                 multiple: true,
                                 renderValue: (selected) => (
                                   selected.length === 0
                                     ? <Typography variant="body2" color="text.secondary">None</Typography>
                                     : (
                                       <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                         {selected.map(v => <Chip key={v} label={v} size="small" />)}
                                       </Box>
                                     )
                                 ),
                               },
                             }}>
                    {(me?.matrix?.global_roles || []).map(r => (
                      <MenuItem key={r} value={r}>
                        <Checkbox checked={permRoles.indexOf(r) > -1} />
                        <ListItemText primary={r} />
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button variant="outlined" sx={{ mt: 1, whiteSpace: "nowrap" }} onClick={savePermRoles}>
                    Save roles
                  </Button>
                </Box>
              </>
            )}

            {/* Sign-in access — Super Admin only, existing records only. The roles
                above say what someone may do once inside; this is whether Entra
                lets them in at all (a guest account + the users group). */}
            {canManageAccess && !isNew && (
              <>
                <Divider textAlign="left">Sign-in access (Super Admin)</Divider>
                <Box sx={{ display: "grid", gap: 1 }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    <Button variant="outlined"
                            startIcon={access.loading ? <CircularProgress size={16} /> : <VpnKeyIcon />}
                            disabled={!loginAddress.includes("@") || access.loading}
                            onClick={checkAccess}>
                      Check sign-in access
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {loginAddress.includes("@")
                        ? `Is ${loginAddress} in the Microsoft tenant and the users group?`
                        : "Needs an email address on the record first."}
                    </Typography>
                  </Box>

                  {access.error && <Alert severity="error">{access.error}</Alert>}

                  {access.result && !access.result.configured && (
                    <Alert severity="info">
                      This server has no directory credentials configured, so access can't be
                      granted from here — invite the guest and add them to the group in the
                      Microsoft Entra portal instead.
                    </Alert>
                  )}

                  {access.result?.configured && !access.result.user && (
                    <Alert severity="warning"
                           action={
                             <Button color="inherit" size="small" disabled={access.loading}
                                     onClick={grantAccess}>
                               Invite &amp; add
                             </Button>
                           }>
                      <AlertTitle>No Microsoft account for this address</AlertTitle>
                      They will be invited as a tenant guest and added to{" "}
                      {access.result.group_name || "the users group"}, which lets them sign in.
                      {/* ml:0 cancels FormControlLabel's default -11px (which would
                          clip the label against the Alert's padding), and the label
                          is a span so it stays on the checkbox's line. */}
                      <FormControlLabel
                        sx={{ display: "flex", ml: 0, mt: 0.5 }}
                        control={<Checkbox size="small" sx={{ p: 0.5, mr: 0.5 }} checked={inviteEmail}
                                           onChange={e => setInviteEmail(e.target.checked)} />}
                        label={<Typography variant="body2" component="span">
                          Send them Microsoft's invitation email
                        </Typography>}
                      />
                    </Alert>
                  )}

                  {access.result?.configured && access.result.user && !access.result.in_group && (
                    <Alert severity="warning"
                           action={
                             <Button color="inherit" size="small" disabled={access.loading}
                                     onClick={grantAccess}>
                               Add to group
                             </Button>
                           }>
                      <AlertTitle>In the tenant, but can't sign in</AlertTitle>
                      {access.result.user.display_name || access.result.email} exists as{" "}
                      {(access.result.user.user_type || "a user").toLowerCase()} but is not in{" "}
                      {access.result.group_name || "the users group"}.
                    </Alert>
                  )}

                  {access.result?.configured && access.result.user && access.result.in_group && (
                    <Alert severity="success" icon={<VpnKeyIcon />}>
                      <AlertTitle>Can sign in</AlertTitle>
                      <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5 }}>
                        <Box component="dt" sx={{ fontWeight: 600 }}>Directory name</Box>
                        <Box component="dd" sx={{ m: 0 }}>{access.result.user.display_name || "—"}</Box>
                        <Box component="dt" sx={{ fontWeight: 600 }}>Account</Box>
                        <Box component="dd" sx={{ m: 0, wordBreak: "break-all" }}>
                          {access.result.user.user_principal_name || "—"}
                        </Box>
                        <Box component="dt" sx={{ fontWeight: 600 }}>Type</Box>
                        <Box component="dd" sx={{ m: 0 }}>{access.result.user.user_type || "—"}</Box>
                        <Box component="dt" sx={{ fontWeight: 600 }}>Group</Box>
                        <Box component="dd" sx={{ m: 0 }}>
                          {access.result.group_name || access.result.group_id}
                        </Box>
                      </Box>
                      {access.result.user.external_user_state === "PendingAcceptance" && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          They haven't accepted the guest invitation yet — that happens
                          automatically the first time they sign in.
                        </Typography>
                      )}
                      {access.result.user.account_enabled === false && (
                        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                          Their directory account is disabled, so sign-in will still fail.
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Box>
              </>
            )}

            {/* Save/Cancel apply to the fields above. Event changes below save instantly. */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
              {!isNew && can("Personnel.delete") && (
                <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button onClick={requestClose}>Cancel</Button>
              <Button variant="contained"
                      disabled={!canSave || saving || (isNew ? !can("Personnel.add") : !canEdit)}
                      startIcon={saving ? <CircularProgress size={16} /> : undefined}
                      onClick={save}>{saving ? "Saving…" : "Save"}</Button>
            </Box>

            {/* Events this person is associated with (saved instantly, independent of Save above) */}
            <Divider textAlign="left">Events</Divider>
            {isNew ? (
              <Typography variant="body2" color="text.secondary">
                Save this person first, then re-open to add events.
              </Typography>
            ) : (
              <>
                {attendances.length > 1 && (
                  <TextField label="Sort by" select size="small" sx={{ minWidth: 200, alignSelf: "flex-start" }}
                             value={attSort} onChange={e => setAttSort(e.target.value)}>
                    <MenuItem value="date_desc">Date (newest first)</MenuItem>
                    <MenuItem value="date_asc">Date (oldest first)</MenuItem>
                  </TextField>
                )}
                {/* Role, status, fee and shift details are edited in a dialog
                    (shared with the Events page); removal lives in there too. */}
                {sortedAttendances.map(a => (
                  <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {a.event ? a.event.label_long : `Event #${a.event_id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{a.role || "No event role set"}</Typography>
                    </Box>
                    {a.status && <Chip size="small" label={a.status} color={STATUS_COLOR[a.status] || "default"} />}
                    {(can("EventAttendances.edit") || can("EventAttendances.delete")) && (
                      <Tooltip title={can("EventAttendances.edit") ? "Edit event details" : "View event details"}>
                        <IconButton size="small" onClick={() => setEditAtt(a)}
                                    aria-label={`Edit ${a.event ? a.event.label_long : "attendance"}`}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))}
                {attendances.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Not associated with any events yet.</Typography>
                )}

                {/* Add an event; set role/status/fee afterwards via the edit (✎) button */}
                {can("EventAttendances.add") && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField select size="small" label="Add event" sx={{ flex: 1 }} value={attDraft.event_id}
                               onChange={e => setAttDraft({ ...attDraft, event_id: e.target.value })}>
                      {events.filter(ev => !attendances.some(a => a.event_id === ev.id))
                             .map(ev => <MenuItem key={ev.id} value={ev.id}>{ev.label_long}</MenuItem>)}
                    </TextField>
                    <Button size="small" variant="outlined"
                            startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
                            disabled={!attDraft.event_id || saving} onClick={addAttendance}>
                      Add
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* Inventory allocated to this person, most recent event first (saved
                instantly, independent of Save above). Gated on the inventory
                permissions rather than the personnel ones: an allocation is part of
                an item's record — see routers/inventory_allocation.py. */}
            {!isNew && can("InventoryItems.view") && (
              <>
                <Divider textAlign="left">Inventory allocations</Divider>
                <AllocationManager
                  personnelId={savedId}
                  canAdd={can("InventoryItems.add")}
                  canEdit={can("InventoryItems.edit")}
                  canDelete={can("InventoryItems.delete")} />
              </>
            )}

            {/* The documents this record used to hold, last of all — it's rare to
                need one. Replacing a CV or a certificate above only moves the
                pointer, so the previous file is still here. */}
            {!isNew && (
              <>
                <Divider textAlign="left">Document history</Divider>
                <PersonnelDocumentHistory
                  personnelId={savedId}
                  version={historyVersion}
                  canDelete={can("Personnel.deleteDocumentHistory")} />
              </>
            )}
          </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Attendance (role / status / fee / shift details) — shared with Events */}
      <AttendanceDialog
        attendance={editAtt}
        subject={editAtt?.event ? editAtt.event.label_long : `Event #${editAtt?.event_id}`}
        onClose={() => setEditAtt(null)}
        onSaved={() => refreshAttendances(savedId)}
        canEdit={can("EventAttendances.edit")}
        canDelete={can("EventAttendances.delete")} />

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete personnel record?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete {form.forename} {form.surname}'s record. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={remove}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved-changes confirmation */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes to this personnel record. If you close now, they will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={close}>Discard changes</Button>
        </DialogActions>
      </Dialog>

      {/* Send SMS / WhatsApp message */}
      <Dialog open={sms.open} onClose={() => !sms.loading && setSms(EMPTY_SMS)} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>Send a message to {form.forename} {form.surname}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DialogContentText>
              The message will be sent to <strong>{form.phone}</strong> from the Shelter Stone
              Event Control number.
            </DialogContentText>
            <TextField
              label="Message"
              fullWidth
              multiline
              minRows={4}
              autoFocus
              disabled={sms.loading}
              value={sms.message}
              onChange={e => setSms(s => ({ ...s, message: e.target.value, error: null }))}
            />
            {sms.error && <Alert severity="error">{sms.error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSms(EMPTY_SMS)} disabled={sms.loading}>Cancel</Button>
          <Tooltip title="WhatsApp sending is coming soon">
            <span>
              <Button variant="outlined" startIcon={<WhatsAppIcon />} disabled>
                Send via WhatsApp
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={sms.loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={!sms.message.trim() || sms.loading}
            onClick={sendSms}
          >
            Send via SMS
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
